import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { IconType } from '@ng-icons/core';
import { debounceTime, distinctUntilChanged, of, Subject, Subscription, switchMap, tap, timer } from 'rxjs';
import { Address } from 'src/app/modules/route-planner/interfaces/location/address.interface';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { AutoCompleteService } from 'src/app/modules/route-planner/services/auto-complete/auto-complete.service';
import { AddressAutoCompleteResultComponent } from './address-auto-complete-result/address-auto-complete-result.component';
import { Overlay } from '@angular/cdk/overlay';
import { DATA_INJECTION_TOKEN } from 'src/app/@shared/services/overlay/overlay.service';
import { SlideUpCardComponent } from 'src/app/@shared/components/design/slide-up-card/slide-up-card.component';
import { AddressAutoCompleteMapComponent } from './address-auto-complete-map/address-auto-complete-map.component';
import { LocationService } from 'src/app/modules/route-planner/services/location/location.service';
import { ModalService } from 'src/app/@shared/services/modal/modal.service';
import { FavoritesService } from 'src/app/modules/route-planner/services/favorites/favorites.service';
import { FavoriteAddresses } from 'src/app/modules/route-planner/interfaces/favorites/favorite-addresses.interface';
import { eFavoriteType } from 'src/app/modules/route-planner/enums/favorite/favorite-type.enum';
import { BaseAddressAutoCompleteComponent } from './base-address-auto-complete.component';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import {
  ASK_FOR_POI,
  CANT_FIND_USER_LOCATION_ADDRESS,
  CLEAR,
  FOR,
  INSERT_ADDRESS,
  MY_LOCATION,
  NO_RESULT_FOUND,
  POI_REQUEST_SENT,
  SEARCH_ON_MAP,
  SET_HOME_ADDRESS,
  SET_WORK_ADDRESS,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { Direction } from '@angular/cdk/bidi';
import { UserLocationService } from 'src/app/@shared/services/user-location/user-location.service';
import { take, takeUntil } from 'rxjs/operators';
import {
  createSlideUpListboxId,
  SlideUpComboboxListKeyboard,
} from '../../../shared/slide-up-combobox-list-keyboard.controller';

export interface RoutePlannerAutoCompleteData {
  address: Address;
  title: string;
  initialValue?: string;
  hideFeed?: boolean;
  isInnerCard?: boolean;
  saveResultToStorage?: boolean;
  /** When true, choosing an address only closes the popup; map overlay stays open (e.g. init map). */
  keepMapOpenOnSelect?: boolean;
  closeCallbackFn?: (value?: any, target?: 'origin' | 'destination' | 'stop') => void;
}

@Component({
  selector: 'app-address-auto-complete',
  templateUrl: './address-auto-complete.component.html',
  styleUrls: ['./address-auto-complete.component.scss'],
  standalone: false
})
export class AddressAutoCompleteComponent
  extends BaseAddressAutoCompleteComponent
  implements OnInit {
  @ViewChild('card') override card: SlideUpCardComponent;
  @ViewChild('input', { read: ElementRef })
  private searchInputHost?: ElementRef<HTMLElement>;

  private destroy$ = new Subject<void>();
  private noResultsTimerSub?: Subscription;

  selectedTabIndex: number = 0;

  favoriteAddresses: FavoriteAddresses;

  searchOnMapText: string;
  askPoiText: string;
  poiRequestSentText: string;
  noResultsText: string;
  insertAddressText: string;
  forText: string;
  clearText: string;
  myLocationText: string;

  missingSearchSent = false;
  missingSearchSentViaButton = false;

  iconName: string;

  isCurrentLocationLoading: boolean = false

  direction: Direction;

  /**
   * a11y: WAI-ARIA combobox-autocomplete-list — keyboard via SlideUpComboboxListKeyboard.
   */
  readonly listboxId = createSlideUpListboxId('mot-address-listbox');
  currentResults: Address[] = [];
  readonly comboboxKeyboard: SlideUpComboboxListKeyboard;

  constructor(
    routePlanningService: RoutePlanningService,
    resizeService: ResizeService,
    overlay: Overlay,
    favoritesService: FavoritesService,

    @Inject(DATA_INJECTION_TOKEN)
    public readonly data: RoutePlannerAutoCompleteData,
    private readonly autoCompleteService: AutoCompleteService,
    private readonly modalService: ModalService,
    private readonly locationService: LocationService,
    private readonly userLocationService: UserLocationService,
    private readonly languageService: LanguageService
  ) {
    super(favoritesService, routePlanningService, resizeService, overlay);
    this.comboboxKeyboard = new SlideUpComboboxListKeyboard(
      this.listboxId,
      () => this.currentResults,
      () => this.isListboxExpanded,
      () => this.searchInputHost?.nativeElement,
      (index) =>
        this.chooseValue(
          this.currentResults[index],
          !!this.data.saveResultToStorage
        )
    );
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.initializeAddressFormControl();

    this.favoritesService.onFavoritesChange$.subscribe((favorites) => {
      this.favoriteAddresses = favorites?.addresses;
    });

    this.initializeMultiLanguageTexts();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy()
    this.noResultsTimerSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }


  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe((language) => {
      this.direction = language.direction;
      this.iconName =
        language.direction === 'rtl' ? 'chevron-right' : 'chevron-left';

      this.searchOnMapText = SEARCH_ON_MAP[language.language];
      this.askPoiText = ASK_FOR_POI[language.language];
      this.poiRequestSentText = POI_REQUEST_SENT[language.language];
      this.noResultsText = NO_RESULT_FOUND[language.language];
      this.forText = FOR[language.language];
      this.insertAddressText = INSERT_ADDRESS[language.language];
      this.clearText = CLEAR[language.language];
      this.myLocationText = MY_LOCATION[language.language];
    });
  }

  private initializeAddressFormControl() {
    if (!!this.data?.address) {
      this.formControl.setValue(this.data.address.addressDetails?.label, {
        emitEvent: false,
      });
    }

    this.formControl.valueChanges
      .pipe(
        tap((val) => {
          this.emptyResults$.next(false);
          this.comboboxKeyboard.resetHighlight();
          this.noResultsTimerSub?.unsubscribe();
          this.missingSearchSent = false;
          this.missingSearchSentViaButton = false;
        }),
        debounceTime(this.debounceTime),
        distinctUntilChanged(),
        switchMap((val) => this.autoCompleteService.searchLocations(val)),
        takeUntil(this.destroy$)
      )
      .subscribe((results) => {
        this.results$ = of(results);
        this.currentResults = (results || []).map((r) => r.value);
        this.emptyResults$.next(results.length === 0);
        this.comboboxKeyboard.resetHighlight();

        if (results.length === 0) {
          const searchText = this.formControl.value;
          this.noResultsTimerSub?.unsubscribe();
          this.noResultsTimerSub = timer(3_000).pipe(
            switchMap(() => this.autoCompleteService.addMissingSearch(searchText)),
            takeUntil(this.destroy$)
          ).subscribe(() => {
            this.missingSearchSent = true;
          });
        }
      });
  }

  get activeOptionId(): string | null {
    return this.comboboxKeyboard.activeOptionId;
  }

  getOptionId(index: number): string {
    return this.comboboxKeyboard.getOptionId(index);
  }

  get activeOptionIndex(): number {
    return this.comboboxKeyboard.activeOptionIndex;
  }

  /** True when the listbox is visible with at least one option (drives aria-expanded). */
  get isListboxExpanded(): boolean {
    return (
      !!this.formControl.value &&
      this.formControl.value.length >= this.autoCompleteMinChar &&
      this.currentResults.length > 0
    );
  }

  onComboboxKeydown(event: KeyboardEvent): void {
    this.comboboxKeyboard.onComboboxKeydown(event);
  }

  onListboxKeydown(event: KeyboardEvent): void {
    this.comboboxKeyboard.onListboxKeydown(event);
  }

  onListboxContainerFocus(event: FocusEvent): void {
    this.comboboxKeyboard.onListboxContainerFocus(event);
  }

  onOptionFocus(index: number): void {
    this.comboboxKeyboard.onOptionFocus(index);
  }

  getAddressIcon(address: Address): IconType {
    if (!this.favoriteAddresses)
      return AddressAutoCompleteResultComponent.DEFAULT_ICON;

    switch (address.addressDetails.label) {
      case this.favoriteAddresses.homeAddress?.addressDetails.label:
        return 'home';
      case this.favoriteAddresses.workAddress?.addressDetails.label:
        return 'briefcase';
      default:
        if (address.isPoi) {
          return 'map-location-dot';
        } else if (address.isStop) {
          return 'station';
        } else if (address.isCity) {
          return 'buildings';
        }
        return AddressAutoCompleteResultComponent.DEFAULT_ICON;
    }
  }

  getAddressSub(address: Address): string {
    if (address.isStop) {
      return address.addressDetails.city;
    } else if (address.isPoi) {
      let sub = ""
      if (address.addressDetails.street) {
        sub += address.addressDetails.street + " "
        if (address.addressDetails.houseNumber) {
          sub += address.addressDetails.houseNumber
        }
      }
      if (address.addressDetails.city) {
        if (address.addressDetails.street) {
          sub += ", "
        }
        sub += address.addressDetails.city
      }
      return sub;
    }
    return address.addressDetails.city;
  }

  handleHomeClick(address: Address) {
    if (!!address) {
      this.chooseValue(address, false);
    } else {
      this.openComponentOverlay(
        this.direction,
        AddressAutoCompleteComponent,
        {
          title: SET_HOME_ADDRESS[this.languageService.getSelectedLanguage().language],
          hideFeed: true,
          isInnerCard: true,
          saveResultToStorage: false,
        },
        this.card.cardElement,
        (address) => {
          if (!!address) {
            this.favoritesService.updateFavorite({
              ...address,
              type: eFavoriteType.HOME_ADDRESS,
            });
          }
        }
      );
    }
  }

  handleWorkClick(address: Address) {
    if (!!address) {
      this.chooseValue(address, false);
    } else {
      this.openComponentOverlay(
        this.direction,
        AddressAutoCompleteComponent,
        {
          title: SET_WORK_ADDRESS[this.languageService.getSelectedLanguage().language],
          hideFeed: true,
          isInnerCard: true,
          saveResultToStorage: false,
        },
        this.card.cardElement,
        (address) => {
          if (!!address) {
            this.favoritesService.updateFavorite({
              ...address,
              type: eFavoriteType.WORK_ADDRESS,
            });
          }
        }
      );
    }
  }

  handleCurrentLocationClick() {
    if (
      this.userLocationService.hasPermissions &&
      this.userLocationService.userLocation
    ) {
      this.isCurrentLocationLoading = true
      const address: Address = {
        addressDetails: {
          label: this.myLocationText,
        },
        coordinates: {
          lat: this.userLocationService.userLocation.lat,
          lng: this.userLocationService.userLocation.lng,
        },
      }
      this.isCurrentLocationLoading = true
      this.chooseValue(address, true);
      // this.autoCompleteService
      //   .getAddressDataByCoordinates(this.userLocationService.userLocation)
      //   .subscribe((res) => {
      //     this.isCurrentLocationLoading = false
      //     if (!!res.success) {
      //       this.chooseValue(res.data, true);
      //     }
      //   });
    } else {
      this.modalService.openCantFindUserLocationModal(CANT_FIND_USER_LOCATION_ADDRESS)
    }
  }

  handleMapClick(initialMapValue?: string) {
    const afterMapClose = (value) => {
      this.routePlanningService._mapOverlaySub.next(null);

      if (!!value) {
        this.chooseValue(value, true);
      }
    };

    if (!!this.isDesktop) {
      this.routePlanningService._mapOverlaySub.next({
        mapComponent: AddressAutoCompleteMapComponent,
        data: {
          ...this.data,
          initialValue: initialMapValue,
          closeCallbackFn: afterMapClose,
        },
      });
    } else {
      this.openComponentOverlay(
        this.direction,
        AddressAutoCompleteMapComponent,
        { ...this.data, initialValue: initialMapValue },
        this.card.cardElement,
        afterMapClose
      );
    }
  }

  sendMissingSearch(): void {
    this.missingSearchSentViaButton = true;
    if (this.missingSearchSent) {
      return;
    }
    this.noResultsTimerSub?.unsubscribe();
    this.autoCompleteService.addMissingSearch(this.formControl.value)
    .pipe(takeUntil(this.destroy$))
    .subscribe(() => {
        this.missingSearchSent = true;
      });
  }

  handleClose() {
    // this.routePlanningService._mapOverlaySub.next(null);
    this.closeCard(this.data?.address);
  }

  override handleOverlayEscape(): void {
    this.handleClose();
  }
}
